<script>
import DataImportsAPI from 'dashboard/api/dataImports';
import { useAlert } from 'dashboard/composables';

// Importação de planilha em três passos: escolher o arquivo, conferir o
// de/para das colunas, e acompanhar.
//
// A leitura é toda do servidor (ver DataImport::SheetReader): o passo de
// pré-visualização manda o arquivo, recebe colunas, amostra e o palpite de
// mapeamento, e não grava nada. O arquivo sobe de novo na confirmação.
export default {
  name: 'ImportWizardModal',
  props: {
    show: { type: Boolean, default: false },
    // Etapas do funil aberto, pra escolher o destino das negociações.
    funnels: { type: Array, default: () => [] },
    activeFunnelId: { type: [String, Number], default: null },
  },
  emits: ['close', 'imported'],
  data() {
    return {
      step: 'file',
      dataType: 'deals',
      file: null,
      isWorking: false,
      errorMessage: '',
      columns: [],
      sample: [],
      fields: [],
      requiredKeys: [],
      mapping: {},
      targetFunnelId: null,
      targetStageId: null,
      skipExistingOpenDeal: true,
      // Painel de criar campo na hora, aberto para uma coluna por vez.
      creatingFor: null,
      newField: { name: '', type: 'text' },
      result: null,
    };
  },
  computed: {
    isDeals() {
      return this.dataType === 'deals';
    },
    fieldOptions() {
      return this.fields.filter(field => !this.isTakenByAnother(field.key));
    },
    selectedFunnel() {
      return this.funnels.find(
        funnel => String(funnel.id) === String(this.targetFunnelId)
      );
    },
    stages() {
      return this.selectedFunnel?.stages || [];
    },
    missingRequired() {
      const used = Object.values(this.mapping).filter(Boolean);
      return this.requiredKeys.filter(key => !used.includes(key));
    },
    missingRequiredLabels() {
      return this.missingRequired
        .map(key => this.fields.find(field => field.key === key)?.label || key)
        .join(', ');
    },
    canImport() {
      if (this.missingRequired.length) return false;
      if (this.isDeals && !this.targetStageId) return false;
      return !this.isWorking;
    },
    newFieldTypes() {
      return [
        { value: 'text', label: 'Texto' },
        { value: 'number', label: 'Número' },
        { value: 'date', label: 'Data' },
        { value: 'link', label: 'Link' },
        { value: 'checkbox', label: 'Caixa de seleção' },
      ];
    },
  },
  watch: {
    show(value) {
      if (value) this.reset();
    },
    dataType() {
      // Os campos de destino mudam entre contato e negócio; o que já estava
      // mapeado não vale mais.
      this.step = 'file';
      this.mapping = {};
      this.columns = [];
    },
  },
  methods: {
    reset() {
      this.step = 'file';
      this.file = null;
      this.errorMessage = '';
      this.columns = [];
      this.sample = [];
      this.mapping = {};
      this.result = null;
      this.creatingFor = null;
      this.targetFunnelId = this.activeFunnelId;
      this.targetStageId = null;
    },
    onFileChange(event) {
      const [file] = event.target.files || [];
      this.file = file || null;
      this.errorMessage = '';
    },
    // Uma coluna por campo: dois "Telefone" apontando pro mesmo destino
    // fariam o segundo sobrescrever o primeiro em silêncio.
    isTakenByAnother(key) {
      if (!key) return false;
      return Object.values(this.mapping).includes(key);
    },
    optionsFor(column) {
      const current = this.mapping[column];
      return this.fields.filter(
        field => field.key === current || !this.isTakenByAnother(field.key)
      );
    },
    // A mesma linha da planilha costuma trazer dado do contato E do negócio.
    // Agrupar deixa explícito onde cada coluna vai parar, em vez de uma lista
    // corrida onde "Nome" e "Título" parecem a mesma coisa.
    groupedOptionsFor(column) {
      const available = this.optionsFor(column);
      const groups = [];

      available.forEach(field => {
        const name = field.group || 'Outros';
        const existing = groups.find(group => group.name === name);
        if (existing) existing.fields.push(field);
        else groups.push({ name, fields: [field] });
      });

      return groups;
    },
    sampleFor(column) {
      return this.sample
        .map(row => row[column])
        .filter(value => value !== '' && value != null)
        .slice(0, 3)
        .join(' · ');
    },
    async loadPreview() {
      if (!this.file) return;

      this.isWorking = true;
      this.errorMessage = '';
      try {
        const { data } = await DataImportsAPI.preview(this.file, this.dataType);
        this.columns = data.columns || [];
        this.sample = data.sample || [];
        this.fields = data.fields || [];
        this.requiredKeys = data.required || [];
        this.mapping = { ...(data.suggested_mapping || {}) };
        this.step = 'mapping';
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error ||
          'Não foi possível ler a planilha. Confira se ela é .csv, .xlsx ou .ods.';
      } finally {
        this.isWorking = false;
      }
    },
    openFieldCreator(column) {
      this.creatingFor = column;
      this.newField = { name: column, type: 'text' };
    },
    cancelFieldCreator() {
      this.creatingFor = null;
    },
    async createFieldForColumn() {
      const column = this.creatingFor;
      const name = this.newField.name.trim();
      if (!name) return;

      this.isWorking = true;
      try {
        const attribute = await this.$store.dispatch('attributes/create', {
          attribute_display_name: name,
          attribute_description: `Importado da coluna "${column}"`,
          attribute_model: this.isDeals
            ? 'deal_attribute'
            : 'contact_attribute',
          attribute_display_type: this.newField.type,
          attribute_key: this.keyFor(name),
          attribute_values: [],
          // Campo nascido de importação não invade a gaveta: entra no
          // expansor "Mais campos" e você promove o que importar depois.
          always_visible: false,
        });

        const key = `custom:${attribute.attribute_key}`;
        this.fields.push({
          key,
          label: attribute.attribute_display_name,
          custom: true,
        });
        this.mapping = { ...this.mapping, [column]: key };
        this.creatingFor = null;
      } catch (error) {
        useAlert(error?.message || 'Não foi possível criar o campo');
      } finally {
        this.isWorking = false;
      }
    },
    keyFor(name) {
      return name
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '')
        .slice(0, 40);
    },
    async startImport() {
      if (!this.canImport) return;

      this.isWorking = true;
      try {
        const settings = this.isDeals
          ? {
              stage_id: this.targetStageId,
              skip_existing_open_deal: this.skipExistingOpenDeal,
            }
          : {};
        const { data } = await DataImportsAPI.start({
          file: this.file,
          dataType: this.dataType,
          mapping: this.mapping,
          settings,
        });
        this.result = data;
        this.step = 'done';
        this.$emit('imported');
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            'Não foi possível iniciar a importação'
        );
      } finally {
        this.isWorking = false;
      }
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="() => $emit('close')">
    <div class="flex flex-col w-full">
      <div class="px-6 py-4 border-b border-n-weak">
        <h2 class="text-lg font-semibold text-n-slate-12 m-0">
          Importar planilha
        </h2>
        <p class="text-sm text-n-slate-11 m-0 mt-1">
          Aceita .csv, .xlsx e .ods, com separador vírgula ou ponto e vírgula.
        </p>
      </div>

      <!-- Passo 1: arquivo -->
      <div v-if="step === 'file'" class="p-6 space-y-4">
        <div>
          <label
            class="block text-xs font-semibold text-n-slate-11 mb-1 uppercase tracking-wide"
          >
            O que você está importando
          </label>
          <select
            v-model="dataType"
            class="w-full px-3 py-2 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12"
          >
            <option value="deals">Negociações</option>
            <option value="contacts">Contatos</option>
          </select>
        </div>

        <div>
          <label
            class="block text-xs font-semibold text-n-slate-11 mb-1 uppercase tracking-wide"
          >
            Planilha
          </label>
          <input
            type="file"
            accept=".csv,.xlsx,.ods"
            class="w-full text-sm text-n-slate-12"
            @change="onFileChange"
          />
        </div>

        <p v-if="errorMessage" class="text-sm text-n-ruby-11 m-0">
          {{ errorMessage }}
        </p>

        <div class="flex justify-end gap-2 pt-2">
          <button
            class="px-3 py-2 text-sm font-medium rounded-md border border-n-weak bg-transparent text-n-slate-11 hover:bg-n-alpha-1 cursor-pointer"
            @click="$emit('close')"
          >
            Cancelar
          </button>
          <button
            class="px-4 py-2 text-sm font-semibold text-white rounded-md border-none cursor-pointer bg-woot-500 hover:bg-woot-600 disabled:opacity-50"
            :disabled="!file || isWorking"
            @click="loadPreview"
          >
            {{ isWorking ? 'Lendo...' : 'Continuar' }}
          </button>
        </div>
      </div>

      <!-- Passo 2: mapeamento -->
      <div v-else-if="step === 'mapping'" class="flex flex-col">
        <div class="px-6 py-3 border-b border-n-weak">
          <p class="text-sm text-n-slate-11 m-0">
            Encontramos {{ columns.length }} colunas. Confira o destino de cada
            uma; o que ficar em "Não importar" é ignorado.
          </p>
        </div>

        <div class="px-6 py-4 max-h-[45vh] overflow-y-auto space-y-3">
          <div
            v-for="column in columns"
            :key="column"
            class="flex items-start gap-3"
          >
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-n-slate-12 m-0 truncate">
                {{ column }}
              </p>
              <p class="text-xs text-n-slate-10 m-0 truncate">
                {{ sampleFor(column) || 'sem exemplos nesta coluna' }}
              </p>
            </div>

            <div class="flex-1 min-w-0">
              <select
                v-model="mapping[column]"
                class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12"
              >
                <option value="">Não importar</option>
                <optgroup
                  v-for="group in groupedOptionsFor(column)"
                  :key="group.name"
                  :label="group.name"
                >
                  <option
                    v-for="field in group.fields"
                    :key="field.key"
                    :value="field.key"
                  >
                    {{ field.label }}{{ field.required ? ' *' : '' }}
                  </option>
                </optgroup>
              </select>
              <button
                class="text-xs text-n-brand hover:underline border-none bg-transparent cursor-pointer px-0 mt-1"
                @click="openFieldCreator(column)"
              >
                Criar campo para esta coluna
              </button>
            </div>
          </div>
        </div>

        <!-- Criar campo na hora -->
        <div
          v-if="creatingFor"
          class="px-6 py-4 border-t border-n-weak bg-n-alpha-1 space-y-3"
        >
          <p class="text-sm font-medium text-n-slate-12 m-0">
            Novo campo para "{{ creatingFor }}"
          </p>
          <div class="flex gap-2">
            <input
              v-model="newField.name"
              type="text"
              placeholder="Nome do campo"
              class="flex-1 px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12"
            />
            <select
              v-model="newField.type"
              class="px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12"
            >
              <option
                v-for="type in newFieldTypes"
                :key="type.value"
                :value="type.value"
              >
                {{ type.label }}
              </option>
            </select>
          </div>
          <p class="text-xs text-n-slate-10 m-0">
            Ele nasce escondido em "Mais campos" na gaveta do negócio. Você
            promove depois, em Configurações.
          </p>
          <div class="flex justify-end gap-2">
            <button
              class="px-3 py-1.5 text-sm rounded-md border border-n-weak bg-transparent text-n-slate-11 cursor-pointer"
              @click="cancelFieldCreator"
            >
              Cancelar
            </button>
            <button
              class="px-3 py-1.5 text-sm font-semibold text-white rounded-md border-none cursor-pointer bg-woot-500"
              :disabled="isWorking"
              @click="createFieldForColumn"
            >
              Criar e vincular
            </button>
          </div>
        </div>

        <!-- Destino -->
        <div v-if="isDeals" class="px-6 py-4 border-t border-n-weak flex gap-3">
          <div class="flex-1">
            <label
              class="block text-xs font-semibold text-n-slate-11 mb-1 uppercase tracking-wide"
            >
              Funil
            </label>
            <select
              v-model="targetFunnelId"
              class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12"
              @change="targetStageId = null"
            >
              <option :value="null" disabled>Selecionar...</option>
              <option
                v-for="funnel in funnels"
                :key="funnel.id"
                :value="funnel.id"
              >
                {{ funnel.name }}
              </option>
            </select>
          </div>
          <div class="flex-1">
            <label
              class="block text-xs font-semibold text-n-slate-11 mb-1 uppercase tracking-wide"
            >
              Etapa de destino
            </label>
            <select
              v-model="targetStageId"
              class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12"
            >
              <option :value="null" disabled>Selecionar...</option>
              <option v-for="stage in stages" :key="stage.id" :value="stage.id">
                {{ stage.name }}
              </option>
            </select>
          </div>
        </div>

        <div v-if="isDeals" class="px-6 pb-3">
          <label
            class="flex items-start gap-2 text-sm text-n-slate-12 cursor-pointer"
          >
            <input
              v-model="skipExistingOpenDeal"
              type="checkbox"
              class="mt-0.5 cursor-pointer"
            />
            <span>
              Não criar negócio para contato que já tem um aberto neste funil
              <span class="block text-xs text-n-slate-10">
                Protege contra importar a mesma planilha duas vezes. As linhas
                puladas aparecem no relatório de rejeitadas.
              </span>
            </span>
          </label>
        </div>

        <div class="px-6 py-4 border-t border-n-weak flex items-center gap-3">
          <p
            v-if="missingRequired.length"
            class="text-sm text-n-ruby-11 m-0 flex-1"
          >
            Falta vincular: {{ missingRequiredLabels }}
          </p>
          <p
            v-else-if="isDeals && !targetStageId"
            class="text-sm text-n-slate-11 m-0 flex-1"
          >
            Escolha a etapa onde as negociações vão entrar.
          </p>
          <p v-else class="text-sm text-n-slate-11 m-0 flex-1">
            Tudo pronto. Linhas sem telefone serão listadas como rejeitadas.
          </p>

          <button
            class="px-3 py-2 text-sm font-medium rounded-md border border-n-weak bg-transparent text-n-slate-11 hover:bg-n-alpha-1 cursor-pointer"
            @click="step = 'file'"
          >
            Voltar
          </button>
          <button
            class="px-4 py-2 text-sm font-semibold text-white rounded-md border-none cursor-pointer bg-woot-500 hover:bg-woot-600 disabled:opacity-50"
            :disabled="!canImport"
            @click="startImport"
          >
            {{ isWorking ? 'Enviando...' : 'Importar' }}
          </button>
        </div>
      </div>

      <!-- Passo 3: enviado -->
      <div v-else class="p-6 space-y-3">
        <p class="text-base font-medium text-n-slate-12 m-0">
          Importação iniciada
        </p>
        <p class="text-sm text-n-slate-11 m-0">
          Ela roda em segundo plano e pode levar alguns minutos. Você recebe um
          e-mail quando terminar, com a lista das linhas que não entraram.
        </p>
        <div class="flex justify-end pt-2">
          <button
            class="px-4 py-2 text-sm font-semibold text-white rounded-md border-none cursor-pointer bg-woot-500"
            @click="$emit('close')"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  </woot-modal>
</template>
